<template>
  <div class="medical-record">
    <v-container fluid class="pa-0">
      <v-layout row wrap>
        <!-- Barra lateral -->
        <v-flex md3 xs12 class="pa-2">
          <v-card>
            <v-list dense>
              <!-- Dados do paciente -->
              <v-hover>
                <v-list-tile slot-scope="{ hover }">
                  <v-list-tile-avatar>
                    <v-icon color="green">fas fa-user</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="list-dense">{{ patientName }}</v-list-tile-title>
                    <small class="grey--text text--darken-1">{{ patientBirthdate }} - Patid: {{ patientId }}</small>
                  </v-list-tile-content>
                  <v-fade-transition>
                    <v-list-tile-action v-if="hover">
                      <v-btn icon @click="dialogPatient = true">
                        <v-icon small color="primary">edit</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-fade-transition>
                </v-list-tile>
              </v-hover>
              <v-divider inset></v-divider>
              <!-- Data do atendimento -->
              <v-hover>
                <v-list-tile slot-scope="{ hover }">
                  <v-list-tile-avatar>
                    <v-icon color="pink">fas fa-calendar</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="list-dense">{{ serviceDate }}</v-list-tile-title>
                    <small class="grey--text text--darken-1">{{ status }}</small>
                  </v-list-tile-content>
                  <v-fade-transition>
                    <v-list-tile-action v-if="hover">
                      <v-btn icon @click="dialogEdit = true">
                        <v-icon small color="primary">edit</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-fade-transition>
                </v-list-tile>
              </v-hover>
              <v-divider inset></v-divider>
              <!-- Médico responsável -->
              <v-list-tile>
                <v-list-tile-avatar>
                  <v-icon color="purple">fas fa-user-md</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title class="list-dense">{{ doctorName }}</v-list-tile-title>
                  <small class="grey--text text--darken-1">{{ doctorCRM }}</small>
                </v-list-tile-content>
              </v-list-tile>
              <v-divider inset></v-divider>
              <!-- Resumo (fora do grupo de formulários) -->
              <v-list-tile :active="selectedFormKey === 'resumo'" @click="selectForm('resumo')" style="cursor:pointer">
                <v-list-tile-avatar>
                  <v-icon color="primary">description</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>Resumo</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-divider inset></v-divider>
              <!-- Formulários fixos e dinâmicos -->
              <v-list-group
                v-model="collapseForms"
                no-action
              >
                <template v-slot:activator>
                  <v-subheader class="menu-header mt-2">
                    <v-icon small class="mr-2" color="primary">description</v-icon>
                    Itens do prontuário
                  </v-subheader>
                </template>
                <v-list-tile
                  v-for="item in allForms"
                  :key="item.key"
                  :active="item.key === selectedFormKey"
                  @click="selectForm(item.key)"
                  style="cursor:pointer"
                >
                  <v-list-tile-avatar>
                    <v-icon color="primary">description</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ item.label }}</v-list-tile-title>
                  </v-list-tile-content>
                  <v-list-tile-action v-if="!fixedForms.some(f => f.key === item.key)">
                    <v-btn icon @click.stop="confirmRemoveDynamicForm(item.key)">
                      <v-icon color="error">delete</v-icon>
                    </v-btn>
                  </v-list-tile-action>
                </v-list-tile>
                <v-list-tile>
                  <v-btn block color="secondary" @click="dialogAddForm = true">
                    <v-icon left>add</v-icon>Adicionar formulário
                  </v-btn>
                </v-list-tile>
              </v-list-group>
              <!-- Histórico do paciente -->
              <v-list-group
                v-model="collapseHistory"
                no-action
              >
                <template v-slot:activator>
                  <v-subheader class="menu-header mt-2">
                    <v-icon small class="mr-2" color="primary">history</v-icon>
                    Histórico do paciente
                  </v-subheader>
                </template>
                <v-expansion-panel v-model="expandedHistoryIndex">
                  <v-expansion-panel-content
                    v-for="(item, idx) in patientHistory"
                    :key="item.iuid"
                  >
                    <div slot="header" class="header-exame">
                      <div
                        class="titulo-exame"
                        :class="{ expandido: expandedHistoryIndex === idx }"
                      >
                        {{ item.summary }}
                      </div>
                      <div class="data-exame">{{ formatDate(item.date) }}</div>
                    </div>
                    <div v-if="item.conclusao" class="conclusao-historico">
                      {{ stripHtml(item.conclusao) }}
                    </div>
                    <div v-if="loadingImages[item.iuid]" class="text-xs-center my-2">
                      <v-progress-circular indeterminate color="primary" size="24" />
                    </div>
                    <div v-if="item.images && item.images.length">
                      <!-- Preview principal: imagem OU vídeo -->
                      <div class="media-preview mb-2">
                        <template v-if="item.videos && item.videos.length > 0 && selectedImageIndex[item.iuid] === item.images.length">
                          <iframe
                            :src="item.videos[0]"
                            width="100%"
                            height="250"
                            style="border:0;background:#000;border-radius:8px;"
                            allowfullscreen
                            mozallowfullscreen
                            msallowfullscreen
                            oallowfullscreen
                            webkitallowfullscreen
                          ></iframe>
                        </template>
                        <template v-else>
                          <v-img
                            :src="item.images[selectedImageIndex[item.iuid] || 0]"
                            height="250"
                            contain
                            class="img-main"
                            @click="openGallery(item)"
                            style="cursor:pointer"
                          />
                        </template>
                      </div>
                      <!-- Miniaturas -->
                      <div class="thumbs-row" v-if="item.images.length > 0">
                        <div
                          v-for="(img, idx) in item.images"
                          :key="'thumb'+idx"
                          class="thumb-cell"
                          @click="selectImage(item.iuid, idx)"
                        >
                          <v-img
                            :src="img"
                            height="50"
                            cover
                            :class="['img-thumb', {selected: selectedImageIndex[item.iuid] === idx}]"
                          />
                        </div>
                        <!-- Miniatura do vídeo -->
                        <div
                          v-if="item.videos && item.videos.length > 0"
                          class="thumb-cell"
                          @click="selectImage(item.iuid, item.images.length)"
                        >
                          <div class="video-thumb img-thumb" :class="{selected: selectedImageIndex[item.iuid] === item.images.length}">
                            <v-icon large color="white" class="video-play-icon">play_circle_filled</v-icon>
                          </div>
                        </div>
                      </div>
                      <!-- Botões de laudo e imagens -->
                      <div class="mt-2 d-flex" v-if="item.type === 'exame'">
                        <v-btn flat small color="success" class="btn-action" :href="getPdfUrl(item.iuid)" target="_blank">
                          <v-icon left small>fas fa-file-pdf</v-icon>
                          VER LAUDO
                        </v-btn>
                        <v-btn flat small color="success" class="btn-action" :to="`/viewer/${item.iuid}`" target="_blank">
                          <v-icon left small>fas fa-images</v-icon>
                          IMAGENS
                        </v-btn>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-list-group>
            </v-list>
          </v-card>
          <!-- Diálogo para escolher formulário dinâmico -->
          <v-dialog v-model="dialogAddForm" max-width="400">
            <v-card>
              <v-card-title>Adicionar formulário</v-card-title>
              <v-card-text>
                <v-list>
                  <v-list-tile
                    v-for="item in dynamicFormsAvailable"
                    :key="item.key"
                    @click="addDynamicForm(item)"
                    style="cursor:pointer"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>{{ item.label }}</v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogAddForm = false">Fechar</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
        <!-- Conteúdo principal -->
        <v-flex md9 xs12 class="pa-2">
          <v-card>
            <template v-if="selectedFormKey === 'resumo'">
              <div class="iframe-container">
                <iframe class="iframe-pdf" frameborder="0" ref="frame" :src="urlReport" @load="load"></iframe>
              </div>
              <div class="loading" v-if="loadingPdf">
                <v-progress-circular indeterminate color="primary" :size="50"></v-progress-circular>
              </div>
              <v-card-actions>
                <v-layout justify-center class="button-container pa-2">
                  <input type="file" ref="pdfInput" accept=".pdf" style="display: none" @change="onFileSelected">
                  <v-btn @click="$refs.pdfInput.click()" v-if="!initProcess">Anexar PDF</v-btn>
                  <v-btn @click="dialogPresetPrint = true" v-if="!initProcess">alterar parâmetros</v-btn>
                  <v-btn @click="updatePresets" v-if="!initProcess">gerar laudo</v-btn>
                  <v-btn @click="publicar" color="success" v-show="initProcess">publicar</v-btn>
                  <dialog-preset-print ref="dialogPresetPrint" v-model="dialogPresetPrint" :id="record && record.iuid" reportType="Prontuario" @apply="gerarLaudo"></dialog-preset-print>
                </v-layout>
              </v-card-actions>
            </template>
            <template v-else>
              <v-card-title class="subheading pb-0">
                {{ selectedForm ? selectedForm.label : 'Prontuário Eletrônico' }}
              </v-card-title>
              <v-card-title class="pt-2">
                <template v-if="selectedForm && selectedForm.components && selectedForm.components.length > 1">
                  <v-btn
                    v-for="(panel, idx) in selectedForm.components"
                    :key="panel.key"
                    :outline="tabActivePanel !== idx"
                    class="ma-1"
                    color="primary"
                    small
                    ripple
                    @click="tabActivePanel = idx"
                  >
                    {{ panel.label }}
                  </v-btn>
                </template>
              </v-card-title>
              <v-divider class="mt-0 mb-0" />
              <v-card-text v-if="selectedForm && selectedForm.components && selectedForm.components[tabActivePanel]">
                <u-form
                  v-model="resultForm"
                  :key='selectedForm.key'
                  :id='selectedForm.key'
                  :json='selectedForm.components[tabActivePanel]'
                />
              </v-card-text>
              <v-card-text v-else>
                <v-alert type="info">Formulário do prontuário não encontrado.</v-alert>
              </v-card-text>
            </template>
          </v-card>
        </v-flex>
      </v-layout>
      <!-- Diálogos de edição -->
      <UfPatient v-model="dialogPatient" :iuid="patient && patient.iuid" />
      <UfEditExamDetail v-model="dialogEdit" :iuid="record && record.iuid" />
      <!-- Diálogo de confirmação de exclusão de formulário dinâmico -->
      <v-dialog v-model="dialogRemoveForm" max-width="400">
        <v-card>
          <v-card-title>Confirmar exclusão</v-card-title>
          <v-card-text>Tem certeza que deseja excluir este formulário? Esta ação não pode ser desfeita.</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialogRemoveForm = false">Cancelar</v-btn>
            <v-btn color="error" text @click="removeDynamicFormConfirmed">Excluir</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <vue-gallery
        :images="galleryImages"
        :index="galleryIndex"
        @close="galleryIndex = null"
      />
      <!-- Snackbar de salvamento -->
      <v-snackbar
        v-model="snackbarSaving"
        :timeout="1000"
        bottom
        right
        elevation="2"
        style="min-width: 180px"
      >
        Salvando...
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import VueGallery from 'vue-gallery'
import axios from 'axios'
import { mapGetters, mapState } from 'vuex'
import UForm from '@/components/form/form'
import UfPatient from '@/components/ultra-fetal/home/Patient'
import UfEditExamDetail from '@/components/util/EditExam'
import _ from 'lodash'
import { diff } from 'deep-object-diff'
import { db, storageReports } from '@/store/firebase'
import roleTypes from '@/utils/roleTypes'
import dialogPresetPrint from '@/pages/ultra-fetal/summary/components/dialogPresetPrint'

const FIXED_FORM_KEYS = ['info', 'solicitacoes', 'prescricoes', 'atestados']
const URL_BASE_REPORT = 'https://report-server-dot-ultracarebr.appspot.com/report/'

export default {
  name: 'medical-record-form',
  components: { UForm, UfPatient, UfEditExamDetail, VueGallery, dialogPresetPrint },
  data () {
    return {
      selectedFormKey: '',
      tabActivePanel: 0,
      resultForm: {},
      loading: true,
      dialogPatient: false,
      dialogEdit: false,
      dialogAddForm: false,
      dynamicFormsAddedKeys: [],
      dialogRemoveForm: false,
      formKeyToRemove: null,
      patientHistory: [],
      loadingImages: {},
      expandedHistoryIndex: null,
      selectedImageIndex: {},
      galleryIndex: null,
      galleryImages: [],
      // Collapse states
      collapseForms: true,
      collapseHistory: true,
      // PDF/Laudo
      initProcess: false,
      loaded: false,
      pdfAnexado: false,
      loadProgress: 0,
      interval: {},
      loadingPdf: false,
      dialogPresetPrint: false,
      // snackbar de salvamento
      saving: false,
      snackbarSaving: false,
      snackbarText: ''
    }
  },
  computed: {
    ...mapGetters('formCustomization', ['getCustomizedForms']),
    ...mapState({
      record: state => state.ultraFetal.gestationInteraction,
      patient: state => state.ultraFetal.patient
    }),
    ...mapState('medicalRecord', ['allPatientInteractions']),
    recordId () {
      return this.$route.params.recordId
    },
    forms () {
      // Todos os forms do grupo prontuario
      return this.getCustomizedForms.components
        ? this.getCustomizedForms.components.filter(f => f.group === 'prontuario')
        : []
    },
    fixedForms () {
      // Formulários fixos
      return this.forms.filter(f => FIXED_FORM_KEYS.includes(f.key))
    },
    dynamicFormsAvailable () {
      // Formulários dinâmicos disponíveis para adicionar
      return this.forms.filter(f => !FIXED_FORM_KEYS.includes(f.key) && !this.dynamicFormsAddedKeys.includes(f.key))
    },
    dynamicFormsAdded () {
      // Formulários dinâmicos já adicionados
      return this.forms.filter(f => this.dynamicFormsAddedKeys.includes(f.key))
    },
    allForms () {
      // Lista de todos os forms exibidos na sidebar (fixos + dinâmicos já adicionados)
      return [
        ...this.fixedForms,
        ...this.dynamicFormsAdded
      ]
    },
    selectedForm () {
      return this.allForms.find(f => f.key === this.selectedFormKey) || this.allForms[0] || null
    },
    patientName () {
      return this.patient && this.patient.name ? this.patient.name : ''
    },
    patientId () {
      return this.patient && this.patient.patId ? this.patient.patId : ''
    },
    patientBirthdate () {
      if (this.patient && this.patient.birthdate) {
        return moment(this.patient.birthdate).format('DD/MM/YYYY')
      }
      return '--/--/----'
    },
    serviceDate () {
      return this.record && this.record.date ? moment(this.record.date).format('DD/MM/YYYY') : '--/--/----'
    },
    status () {
      return this.record && this.record.status ? this.record.status : '--'
    },
    doctorName () {
      if (this.record && this.record.doctor) {
        return this.record.doctor.name || this.record.resource || '--'
      }
      return '--'
    },
    doctorCRM () {
      if (this.record && this.record.doctor) {
        return this.record.doctor.identity ? `CRM: ${this.record.doctor.identity}` : ''
      }
      return ''
    },
    urlReport () {
      if (!this.initProcess) return ''
      const { iuid } = this.record || {}
      if (!iuid) return ''
      return URL_BASE_REPORT + iuid
    },
    canEditReport () {
      // Admins e gerentes sempre podem editar
      if ([roleTypes.admin, roleTypes.manager].includes(this.$store.state.user.role)) {
        return true
      }
      // Médicos podem editar se for responsável
      if (roleTypes.doctor === this.$store.state.user.role) {
        if (!this.record.doctor) return true
        if (this.record.doctor && this.record.doctor.id === this.$store.state.user.uid) return true
      }
      return false
    },
    canPublishReport () {
      const isAdminOrManager = [roleTypes.admin, roleTypes.manager].includes(this.$store.state.user.role)
      const isResponsibleDoctor = (
        roleTypes.doctor === this.$store.state.user.role &&
        this.record.doctor &&
        this.record.doctor.id === this.$store.state.user.uid
      )
      return (isAdminOrManager || isResponsibleDoctor) && this.record.status === 'ANDAMENTO'
    },
    shouldShowPdf () {
      return (
        (this.loaded && this.initProcess) ||
        (this.record.status === 'PUBLICADO')
      )
    },
    shouldShowForm () {
      return this.record && this.record.status !== 'PUBLICADO' && this.canEditReport && !this.initProcess
    }
  },
  watch: {
    'record.forms': {
      handler (val) {
        if (val && !_.isEqual(val, this.resultForm)) {
          this.resultForm = { ...val }
        }
      },
      deep: true
    },
    resultForm: {
      handler (val) {
        this.save()
      },
      deep: true
    },
    selectedFormKey (val) {
      // Sempre que trocar de formulário, zera a aba de painel
      this.tabActivePanel = 0
    },
    allForms (val) {
      // Se o formulário selecionado não existir mais, seleciona o primeiro fixo
      if (!val.some(f => f.key === this.selectedFormKey)) {
        this.selectedFormKey = val.length ? val[0].key : ''
      }
    },
    expandedHistoryIndex (val) {
      if (val !== null && this.patientHistory[val]) {
        this.onExpandHistoryItem(true, this.patientHistory[val])
      }
    }
  },
  mounted () {
    this.loading = true
    if (this.recordId) {
      this.$store.dispatch('ultraFetal/getGestationInteraction', this.recordId).then(() => {
        if (this.record && this.record.forms) {
          // Mantém a estrutura plana de chaves e valores
          this.resultForm = { ...this.record.forms }
          // Inicializa os forms dinâmicos já adicionados a partir dos dados salvos
          this.dynamicFormsAddedKeys = Object.keys(this.resultForm)
            .filter(key => key.endsWith('.hasData'))
            .map(key => key.replace('.hasData', ''))
            .filter(key => this.forms.some(f => !FIXED_FORM_KEYS.includes(f.key) && key === f.key))
        } else {
          // Inicializa com objeto vazio
          this.resultForm = {}
        }
        // Seleciona o primeiro formulário fixo por padrão
        this.selectedFormKey = this.fixedForms.length ? this.fixedForms[0].key : (this.allForms[0] ? this.allForms[0].key : '')
        // Busca paciente se necessário
        if (!this.patient || !this.patient.iuid) {
          this.$store.dispatch('ultraFetal/getPatient', this.record.patient.id).then(() => {
            this.$store.dispatch('medicalRecord/getAllPatientInteractions', this.patient.iuid).then(() => {
              this.buildPatientHistory()
            })
          })
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    }
  },
  methods: {
    save: _.debounce(function () {
      if (!this.record || !this.record.iuid) return

      const formChanges = diff(this.record.forms || {}, this.resultForm)
      if (_.isEmpty(formChanges)) return

      this.saving = true
      this.snackbarSaving = true

      this.$store.dispatch('medicalRecord/saveFormData', {
        recordId: this.record.iuid,
        formData: formChanges
      }).then(() => {
        this.saving = false
        // snackbarSaving será fechado automaticamente pelo timeout
      }).catch(error => {
        this.saving = false
        this.snackbarSaving = false
        console.error('Erro ao salvar formulário:', error)
      })
    }, 1500),
    addDynamicForm (form) {
      if (!this.dynamicFormsAddedKeys.includes(form.key)) {
        this.dynamicFormsAddedKeys.push(form.key)
        // Não cria nenhuma key no resultForm
        this.selectedFormKey = form.key
      }
      this.dialogAddForm = false
    },
    selectForm (key) {
      this.selectedFormKey = key
      // Não cria nenhuma key no resultForm
    },
    buildPatientHistory () {
      console.log('buildPatientHistory', this.allPatientInteractions)
      // Monta histórico a partir do state do módulo medicalRecord
      if (!this.allPatientInteractions) return
      const history = this.allPatientInteractions
        .filter(item => item.iuid !== (this.record && this.record.iuid))
        .map(item => {
          let type = 'atendimento'
          let summary = ''
          let conclusao = ''
          if (item.studies && Object.keys(item.studies).length > 0) {
            type = 'exame'
            summary = Object.values(item.studies).map(s => s.title).join(', ')
            if (item.forms && item.forms['conclusion.info.text']) {
              conclusao = item.forms['conclusion.info.text']
            }
          } else if (item.status) {
            summary = item.status
            if (item.observations) {
              conclusao = item.observations
            }
          }
          return {
            iuid: item.iuid,
            date: item.date ? (item.date.toDate ? item.date.toDate() : item.date) : null,
            type,
            summary,
            conclusao
          }
        })
        .sort((a, b) => b.date - a.date)
      console.log('history', history)
      this.patientHistory = history
    },
    confirmRemoveDynamicForm (key) {
      this.formKeyToRemove = key
      this.dialogRemoveForm = true
    },
    removeDynamicFormConfirmed () {
      if (this.formKeyToRemove) {
        this.removeDynamicForm(this.formKeyToRemove)
        this.formKeyToRemove = null
      }
      this.dialogRemoveForm = false
    },
    removeDynamicForm (key) {
      // Remove da lista de adicionados
      this.dynamicFormsAddedKeys = this.dynamicFormsAddedKeys.filter(k => k !== key)
      // Remove do resultForm
      this.$delete(this.resultForm, key)
      // Se o formulário removido estava selecionado, seleciona o primeiro disponível
      if (this.selectedFormKey === key) {
        this.selectedFormKey = this.allForms.length ? this.allForms[0].key : ''
      }
      this.save()
    },
    goToInteraction (item) {
      // Navega para o prontuário/exame selecionado
      this.$router.push({ name: 'Ultra Fetal Gestation Interaction', params: { gestationInteractionId: item.iuid } })
    },
    formatDate (date) {
      if (!date) return '--/--/----'
      return moment(date).format('DD/MM/YYYY')
    },
    getPdfUrl (iuid) {
      return `https://report-server-dot-ultracarebr.appspot.com/report/${iuid}`
    },
    stripHtml (html) {
      if (!html) return ''
      // Remove tags HTML
      const text = html.replace(/<[^>]+>/g, '')
      // Decodifica entidades HTML
      const doc = new window.DOMParser().parseFromString(text, 'text/html')
      let plain = doc.body.textContent || ''
      // Remove quebras de linha, tabulações e múltiplos espaços
      plain = plain.replace(/[\n\r\t]+/g, ' ')
      plain = plain.replace(/\s\s+/g, ' ')
      // Remove espaços no início e fim
      return plain.trim()
    },
    async onExpandHistoryItem (expanded, item) {
      if (expanded && item.type === 'exame' && !item.images) {
        this.$set(this.loadingImages, item.iuid, true)
        try {
          const response = await axios.get(`https://app.ultrahealth.care/functions/patientPortal?id=${item.iuid}`)
          const data = response.data
          // Imagens
          this.$set(item, 'images', data.imagesList || [])
          // Vídeos
          this.$set(item, 'videos', data.videosList || [])
          // NÃO adicionar vídeo na lista de imagens!
          // Atualiza laudo/conclusão se vier diferente
          if (data.forms && data.forms['conclusion.info.text']) {
            item.conclusao = data.forms['conclusion.info.text']
          }
        } catch (e) {
          this.$set(item, 'images', [])
          this.$set(item, 'videos', [])
        } finally {
          this.$set(this.loadingImages, item.iuid, false)
        }
      }
    },
    selectImage (iuid, idx) {
      this.$set(this.selectedImageIndex, iuid, idx)
    },
    openGallery (item) {
      this.galleryImages = item.images
      this.galleryIndex = this.selectedImageIndex[item.iuid] || 0
    },
    async gerarLaudo () {
      if (this.pdfAnexado) {
        this.pdfAnexado = false
      }
      this.loadProgress = 0
      this.loaded = false
      this.initProcess = true
      this.loadingPdf = true
      setTimeout(() => {
        if (this.initProcess) {
          if (this.$refs.frame) {
            this.$refs.frame.src = this.urlReport
          }
        }
        this.startProgress()
      }, 2000)
    },
    async publicar () {
      if (!this.patient || !this.patient.patId) {
        alert('Não é possível publicar exames sem Código (PatId) de paciente. Favor preencher no cadastro do Paciente')
        return
      }
      if (!this.record.forms || !this.record.forms['gestationalAge.info.accessionNumber']) {
        if (!this.record.studiesList && !this.record.studies) {
          alert('Não é possível publicar exames sem Código de exame (AN). Favor preencher no formulário "Informações"')
          return
        } else this.insertAccessionNumberFromStudies()
      }
      this.insertStudyType()
      if (this.pdfAnexado) {
        this.loadingPdf = true
        const file = this.$refs.pdfInput.files[0]
        const storageRef = storageReports.ref(`${this.record.iuid}.pdf`)
        try {
          await storageRef.put(file)
          console.log('PDF uploaded successfully!')
        } catch (error) {
          console.error('Error uploading PDF:', error)
          alert('Erro ao fazer upload do PDF.')
          this.loadingPdf = false
          return
        }
        this.loadingPdf = false
      }
      this.saveStatus('PUBLICADO').then(() => {
        this.$router.push('/studies')
      })
    },
    insertAccessionNumberFromStudies () {
      Object.assign(this.resultForm, {'gestationalAge.info.accessionNumber': Object.keys(this.record.studies)[0]})
      db.doc('gestationInteraction/' + this.record.iuid)
        .update({ forms: this.resultForm }).then(() => {
          this.record.forms['gestationalAge.info.accessionNumber'] = Object.keys(this.record.studies)[0]
        })
    },
    insertStudyType () {
      if (this.resultForm) {
        this.resultForm['studyType'] = 'Prontuário'
        db.doc('gestationInteraction/' + this.record.iuid)
        .update({ forms: this.resultForm }).then(() => {
          this.record.forms['studyType'] = 'Prontuário'
        })
      }
    },
    saveStatus: function (status) {
      return db.doc('gestationInteraction/' + this.record.iuid)
        .update({ status }).then(() => {
          this.record.status = status
        })
    },
    startProgress () {
      this.interval = setInterval(() => {
        if (this.loadProgress === 100 || this.loaded === true) {
          clearInterval(this.interval)
        }
        this.loadProgress += 2
      }, 1000)
    },
    load () {
      if (!this.record.iuid) return
      if (this.initProcess || (!this.canEditReport && this.record.status === 'PUBLICADO')) {
        this.loaded = true
        this.loadingPdf = false
        clearInterval(this.interval)
        this.loadProgress = 100
      }
    },
    onFileSelected (event) {
      const file = event.target.files[0]
      if (file) {
        this.pdfAnexado = true
        this.record.status = 'ANDAMENTO'
        this.initProcess = true
        this.$nextTick(() => {
          this.$refs.frame.src = URL.createObjectURL(file)
          this.loaded = true
        })
      }
    },
    updatePresets () {
      this.$refs.dialogPresetPrint.save()
    }
  }
}
</script>

<style scoped>
.medical-record {
  display: flex;
  flex-direction: column;
}
.list-dense {
  height: 18px;
  line-height: 24px;
}
.conclusao-historico {
  font-size: 15px;
  color: #222;
  margin-bottom: 16px;
  margin-top: 8px;
  line-height: 1.5;
}
.img-main {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  background: #fff;
  border: 1px solid #eee;
}
.thumbs-row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  width: 100%;
  gap: 0;
  height: 50px;
}
.thumb-cell {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  cursor: pointer;
}
.img-thumb {
  border-radius: 0;
  border: 2px solid transparent;
  transition: border 0.2s, box-shadow 0.2s;
  width: 100%;
  height: 100% !important;
  object-fit: cover;
  margin: 0;
}
.img-thumb.selected {
  border: 2px solid #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
}
.btn-action {
  min-width: 140px;
  margin-right: 12px;
  font-weight: bold;
  letter-spacing: 1px;
}
.header-exame {
  display: flex;
  align-items: center;
  width: 100%;
}
.titulo-exame {
  font-size: 16px;
  color: #1976d2;
  font-weight: bold;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0;
}
.titulo-exame.expandido {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
.data-exame {
  font-size: 14px;
  color: #888;
  margin-bottom: 8px;
  margin-left: 12px;
  flex-shrink: 0;
}
.video-thumb {
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.video-play-icon {
  font-size: 36px;
  opacity: 0.8;
}
/* Alinhamento dos menus da sidebar */
.menu-header {
  display: flex;
  align-items: center;
  font-size: 15px;
  padding-left: 8px;
  padding-right: 8px;
}
/* Remove padding extra do grupo de formulários */
.v-list-group__items {
  padding-right: 0 !important;
}

/* Remove margem/padding dos tiles da lista de formulários */
/* (Regra removida, agora controlada pelo CSS global) */

/* Corrige padding extra do Vuetify em grupos sem ação - removido pois está no CSS global */
.iframe-container {
  height: 100%;
  width: 100%;
  min-height: 70vh;
  position: relative;
}
.iframe-pdf {
  height: 100%;
  width: 100%;
  border: none;
  min-height: 70vh;
}
.v-card {
  height: 100%;
}
.v-flex.md9 {
  height: calc(100vh - 100px);
}
</style>

<style>
.v-list__group__items--no-action .v-list__tile {
  padding-left: 8px !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/medical-record/MedicalRecord.vue